/**
 * Moz Adapter — Story 20.7 (opens the Moz slice of Epic 20)
 *
 * **Auth model:** Per-user API token sent as a custom `x-moz-token` HTTP
 * header. The customer pastes their Moz API token (generated in the Moz API
 * dashboard, up to 5 active tokens per account) into the Connect dialog; we
 * encrypt it via the same `encryptToken` helper that OAuth tokens use and
 * store it in `oauth_tokens` under `provider="moz"`. See AC #3 in the Story
 * 20.7 spec for why we deliberately reuse the OAuth table for an API token.
 *
 * **DELIBERATE divergences from Semrush (Story 20.4) — internalize before
 * reaching for any Semrush file as a copy template:**
 *
 *  1. **Auth shape — `x-moz-token` HEADER, NOT `?key=` query param.** Moz
 *     V3 sends the token via a custom HTTP header on every request per
 *     moz.com/api/docs/guides/authentication verified 2026-05-10. The token
 *     value never appears in the URL — but error message echoes from the
 *     upstream JSON-RPC envelope CAN still contain it (if Moz echoes back
 *     what we sent in a malformed-request error). The `safeFetchMoz` wrapper
 *     redacts at the log boundary (Round 4 Red Team Attack #1).
 *
 *  2. **Wire format — JSON-RPC 2.0 envelope (NOT REST).** Every Moz V3 call
 *     is a POST to `https://api.moz.com/jsonrpc` with `{ jsonrpc: "2.0", id,
 *     method, params: { data: {...} } }`. The adapter ships its own JSON-RPC
 *     client at `json-rpc.ts` handling: UUIDv4 `id` generation (Moz requires
 *     ≥24 chars per docs), `result` envelope unwrapping, `error` envelope
 *     shape, async-call envelope auto-routing.
 *
 *  3. **Async-call envelope (Brand Authority).**
 *     `data.site.metrics.brand.authority.fetch` returns `result.async_call.id`
 *     immediately and requires polling via `data.async-call.fetch` with
 *     1s/2s/4s/8s/15s exponential backoff up to 30 sec. Polling is NOT free
 *     (1 row per poll attempt). The JSON-RPC client auto-routes — callers
 *     receive the unwrapped final result.
 *
 *  4. **Snapshot-only API → ProvenLeap snapshot persistence (AC #16).** Moz
 *     V3 has NO historical-time-series surface. To chart "DA over time"
 *     (flagship Story 20.9 cross-SEO-tool comparison use case), the adapter
 *     writes every successful `data.site.metrics.fetch` snapshot to the new
 *     `moz_authority_snapshots` D1 table via `ctx.waitUntil(persistMozSnapshot(...))`.
 *     **This is a build-to-sell decision** per build-to-sell-directive.md
 *     — a buyer's diligence team will look for evidence we built historical
 *     infrastructure ourselves where the upstream API doesn't provide it.
 *
 *  5. **Self-serve token rotation (UNLIKE SEMRUSH).** Moz customers can
 *     generate up to 5 active tokens at any time and regenerate revoked ones
 *     via the Moz API dashboard (no support ticket required) per
 *     moz.com/api/docs/guides/authentication verified 2026-05-10. Semrush
 *     requires a support ticket; the user-facing copy in error messages
 *     reflects the divergence ("Re-paste your token from the Moz API
 *     dashboard" vs Semrush's "Contact Semrush support to rotate").
 *
 * **No 30-day data-persistence ToS cap (DIVERGENT from Semrush).** Verified
 * moz.com/legal/terms 2026-05-10 has no equivalent to Semrush ToS §3.3.
 * Moz uses the global 7-day stale fallback at cache.ts:138; NO entry in
 * `STALE_EXPIRATION_TTL_BY_PLATFORM`. Document this absence in
 * `cache-policy.ts` so a future agent doesn't try to "fix" it by adding a
 * bogus cap.
 *
 * **`API_KEY_PLATFORMS` divergence (AC #11 + Task 1.10):** `"moz"` is NOT
 * added to `API_KEY_PLATFORMS` in adapter.ts. That set is reserved for
 * env-binding API-key platforms (currently `["google-pagespeed"]`). Moz is
 * per-user-secret like Ahrefs + Semrush, NOT env-key. Document this
 * divergence here so a future agent doesn't try to "fix" it.
 *
 * **Future Moz keyword-data methods (AC #17, deferred to Story 20.7a / 20.8):**
 * `data.keyword.metrics.fetch.all`, `data.keyword.searchIntent.fetch`,
 * `data.site.ranking-keywords.list` are catalogued in `MOZ_METHOD_NAMES`
 * (json-rpc.ts) but NOT shipped in Story 20.7. Adding them is purely
 * additive — new helpers in `api.ts` + new transformers + new `MetricDefinition`
 * rows in `MOZ_METRICS`. The mozJsonRpcCall signature is method-agnostic.
 */

import {
  adapterErr,
  adapterOk,
  type AccountInfo,
  type AdapterContext,
  type AdapterMetrics,
  type AdapterResult,
  type DimensionDefinition,
  type EndpointDiscoveryInfo,
  type IntegrationAdapter,
  type MetricDefinition,
  type OAuthCredentials,
} from "../adapter";
import type { KVStore } from "../cache";
import type { Platform } from "@provenleap/db";
import { API_VERSIONS } from "../config";

import {
  fetchMozAnchorText,
  fetchMozBrandAuthority,
  fetchMozIndexMetadata,
  fetchMozLinkingRootDomains,
  fetchMozSiteMetrics,
  fetchMozTopPages,
  validateMozConnection,
  type MozAnchorTextSort,
} from "./api";
import { MOZ_JSON_RPC_BASE } from "./json-rpc";
import { isMozScope, normalizeMozTarget, type MozScope } from "./normalize";
import { sanitizeMozErrorMessage } from "./errors";
import {
  checkMozRowBudget,
  incrementMozRowsUsed,
  MOZ_DAILY_TENANT_ROW_BUDGET,
  MOZ_LIST_LIMIT_MAX,
  todayUtcYyyyMmDd,
} from "./row-budget";
import { persistMozSnapshotFireAndForget, type MozD1Binding } from "./snapshots";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "moz" as const;

export const MOZ_PLATFORM_VALUES = ["moz"] as const;
export type MozPlatform = (typeof MOZ_PLATFORM_VALUES)[number];

export const MOZ_API_VERSION = API_VERSIONS.moz;

export { MOZ_JSON_RPC_BASE };

/**
 * Default `limit` for autonomous-sync calls to list-style methods. Matches
 * `MOZ_LIST_LIMIT_MAX` (50) — the agency-reporting "top 50 pages" convention
 * documented in spec AC #4. The widget-config UI exposes a per-widget Top-N
 * picker that overrides this.
 *
 * **Round 1 review note:** initial implementation halved this to 25 (defensive
 * default) but spec AC #4 expects 50; auditor flagged it as a row-count
 * contract violation. Reverted to 50 to match the documented contract.
 */
export const MOZ_AUTONOMOUS_LIST_LIMIT = 50;

/**
 * Hours of staleness stamped on `metadata.dataFreshnessLagHours` by the
 * fixture-mode helper. Reflects Moz Index's at-most-daily crawl cadence
 * (the Index itself refreshes ~monthly, but per-page recrawls happen
 * daily).
 *
 * Code Review Pass 1 BH-12 — relocated to the constants block so the
 * single literal is discoverable; previously declared just-in-time above
 * `loadMozFixture` where it was easy to miss.
 */
export const MOZ_FIXTURE_LAG_HOURS = 24;

/**
 * Demo workspace's Moz target domain. Shared single-source-of-truth between
 * the seed script (`apps/server/src/services/demo-seed.ts`) and the widget
 * renderer (`apps/web/src/lib/widgetPresets.ts`). A mismatch between the
 * two would silently empty the DA Trend chart.
 *
 * Code Review Pass 2 BH2-8 — moved from `apps/server` to the integrations
 * package so the web app can import it cleanly (`apps/web` cannot import
 * from `apps/server`). Both consumers now reference the same literal.
 */
export const MOZ_DEMO_SNAPSHOT_TARGET = "greenlifewellness.com";
export const MOZ_DEMO_SNAPSHOT_SCOPE = "root_domain";

/**
 * Number of monthly snapshots seeded per AC #8 (Round 4 ADR-C — 6 monthly
 * snapshots Dec-2025 → May-2026).
 */
export const MOZ_DEMO_SNAPSHOT_ROW_COUNT = 6;

/**
 * Story 35.2 — autonomous-sync sort for Anchor Text. `page_authority` mirrors
 * Story 20.7's `fetchMozTopPages` autonomous-sync default and the agency
 * reporting convention of "rank top anchors by the strongest pages".
 */
export const MOZ_AUTONOMOUS_ANCHOR_TEXT_SORT: MozAnchorTextSort = "page_authority";

/**
 * Story 35.2 — classify a Moz 403 as a paid-tier `subscription_required`
 * outcome vs a generic auth/permission failure.
 *
 * **CR Round 1 #4 (HIGH) fix.** Original substring-only classifier
 * false-positived on legitimate 403s like "your account plan was suspended"
 * (real auth failure) — which would permanently lock out the feature for
 * customers who DID pay for it. Tightened to:
 *
 * 1. **Primary path — exact code allowlist.** Moz documents these codes
 *    explicitly for entitlement gating per
 *    moz.com/api/docs/guides/error-handling verified 2026-05-19/2026-05-20.
 * 2. **Secondary path — code + substring AND match.** For Moz responses
 *    whose codes drift outside the documented allowlist, we still classify
 *    as subscription_required, but only when the code field is non-empty
 *    AND contains one of the documented substrings. This protects against
 *    free-form `error.message` text accidentally triggering the classifier.
 *
 * **CR Round 1 #8 (MEDIUM) fix.** Also accept the JSON-RPC envelope path
 * where Moz returns the entitlement code in the error envelope's `code`
 * field while the outer HTTP status is 200 (Moz JSON-RPC convention). The
 * status check is relaxed to `status === 403 || status === 200` to handle
 * this — both shapes are documented at moz.com/api/docs/guides/error-handling.
 *
 * Mirrors Yelp Story 23.5's lazy-first-call entitlement classifier but
 * with stricter false-positive guards (Yelp's classifier was looser; Moz's
 * blast radius is larger because the persist locks out the feature for the
 * tenant lifetime).
 */
const MOZ_SUBSCRIPTION_REQUIRED_CODES = new Set<string>([
  "endpoint_not_in_plan",
  "subscription_required",
  "tier_required",
  "feature_not_in_plan",
  "plan_required",
]);

const MOZ_SUBSCRIPTION_REQUIRED_SUBSTRINGS = [
  "plan",
  "subscription",
  "tier",
  "entitle",
] as const;

/**
 * Story 35.2 — entitlement-classification result tuple. Distinguishes the
 * primary (high-confidence) classifier paths from the secondary message-only
 * fallback. The orchestrator uses this to decide whether to PERSIST
 * `anchorTextEntitled: false` to KV (only on `confidence === "high"`) vs
 * surface a one-call warning without persisting (on `confidence === "low"`)
 * to defend against transient verification-system 403s mistakenly locking
 * out customers for 24h. CR Round 3 #7 fix.
 */
type SubscriptionClassification = {
  readonly matched: boolean;
  readonly confidence: "high" | "low";
};

function classifyMozSubscriptionRequired403(
  err: unknown,
): SubscriptionClassification {
  const e = err as { status?: number; code?: string; message?: string } | undefined;
  if (!e) return { matched: false, confidence: "high" };
  if (e.status !== 403 && e.status !== 200) {
    return { matched: false, confidence: "high" };
  }

  const code = (e.code ?? "").trim().toLowerCase();
  const message = (e.message ?? "").trim().toLowerCase();

  // Primary path — exact documented code allowlist. HIGH confidence; persist
  // the lockout.
  if (code.length > 0 && MOZ_SUBSCRIPTION_REQUIRED_CODES.has(code)) {
    return { matched: true, confidence: "high" };
  }
  // Primary-substring path — code is non-empty AND contains a documented
  // substring. HIGH confidence (Moz's documented codes have these substrings
  // in their roots; a code like "subscription_not_active" would match).
  if (code.length > 0) {
    if (MOZ_SUBSCRIPTION_REQUIRED_SUBSTRINGS.some((s) => code.includes(s))) {
      return { matched: true, confidence: "high" };
    }
    return { matched: false, confidence: "high" };
  }
  // Message-only fallback — code missing entirely. LOW confidence; surface
  // warning but DO NOT persist `anchorTextEntitled: false` (next call
  // retries). Requires >=2 entitlement substrings in the message to defend
  // against false-positives on "malformed query plan" / "your plan tier
  // check failed" / similar incidental phrases.
  if (e.status === 403 && message.length > 0) {
    const matchCount = MOZ_SUBSCRIPTION_REQUIRED_SUBSTRINGS.filter((s) =>
      message.includes(s),
    ).length;
    if (matchCount >= 2) {
      return { matched: true, confidence: "low" };
    }
  }
  return { matched: false, confidence: "high" };
}

// Note: the old boolean `looksLikeSubscriptionRequired403` shim was removed
// per CR Round 3 #7 — `classifyMozSubscriptionRequired403` is now the only
// classifier consumer because the orchestrator branches on the
// `confidence` field to gate the `anchorTextEntitled: false` persist.

// ============================================================================
// Helpers
// ============================================================================

function getAdapterKv(ctx: AdapterContext): KVStore | undefined {
  return (ctx.env as { KV?: KVStore } | undefined)?.KV;
}

function getAdapterDb(ctx: AdapterContext): MozD1Binding | undefined {
  const db = (ctx.env as { DB?: unknown } | undefined)?.DB;
  if (db === undefined || db === null) return undefined;
  return db as MozD1Binding;
}

function emitStructuredLog(event: string, payload: Record<string, unknown>): void {
  console.log(JSON.stringify({ event, ...payload, timestamp: new Date().toISOString() }));
}

function getCtxWaitUntil(
  ctx: AdapterContext,
): ((promise: Promise<unknown>) => void) | undefined {
  const wu = (ctx as unknown as { waitUntil?: unknown }).waitUntil;
  return typeof wu === "function" ? (wu as (p: Promise<unknown>) => void) : undefined;
}

/**
 * Story 35.2 — persist a partial Moz integration metadata patch to the KV
 * side-channel at `moz:meta:{integrationId}`. Used by the anchor-text
 * entitlement probe to flip `anchorTextEntitled` to true/false after the
 * first-call response.
 *
 * Read-modify-write semantics: reads the existing JSON object (if any),
 * shallow-merges the patch, writes the result. NEVER overwrites unrelated
 * keys like `brandAuthorityEntitled` from Story 20.7.
 *
 * **CR Round 2 #1 (CRITICAL) fix — freshness-aware metadata persistence.**
 * Round 1's race-guard was too defensive: it permanently locked
 * `anchorTextEntitled: false` even after a customer upgraded their plan
 * (Moz billing propagation lag breaks the no-overwrite invariant). The new
 * model stamps `anchorTextProbedAt` (epoch ms) at write time. When the
 * patch carries `anchorTextEntitled` and the existing probe is older than
 * `MOZ_ANCHOR_TEXT_REPROBE_TTL_MS` (24 hours), the patch wins — letting a
 * customer who upgraded their plan get the feature back within 24 hours of
 * the first widget render after upgrade. Within the 24-hour window, the
 * race-guard from Round 1 still applies (existing concrete value wins) to
 * prevent lost-update races between concurrent probes.
 *
 * For non-entitlement keys (e.g. a future Story patches an unrelated
 * metadata key), the shallow merge default (patch overrides existing)
 * applies — only `anchorTextEntitled` carries the staleness logic.
 *
 * Fire-and-forget — wrap in `ctx.waitUntil(...)` so a KV write failure does
 * NOT block fetchMetrics. KV writes are best-effort; the next first-call
 * will re-probe if the persist failed (preserves the `undefined` -> retry
 * semantics).
 */
const MOZ_ANCHOR_TEXT_REPROBE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

async function persistMozMetadata(
  kv: KVStore,
  integrationId: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const key = `moz:meta:${integrationId}`;
  let existing: Record<string, unknown> = {};
  try {
    const raw = await kv.get(key);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          existing = parsed as Record<string, unknown>;
        }
      } catch {
        // Malformed JSON in KV — overwrite with patch only (existing stays {}).
      }
    }
  } catch {
    // KV read failure — proceed with empty existing; patch will be written.
  }
  // CR Round 2 #1 + CR Round 3 #1/#2/#3/#11 fixes — freshness-aware merge.
  //
  //   * For `anchorTextEntitled`: stale-probe path (existing probe > 24h old)
  //     accepts the patch (plan-upgrade recovery). Fresh existing differs →
  //     preserve (race-loser path). Idempotent re-probe is a no-op (R3-2).
  //   * `anchorTextProbedAt` is FORBIDDEN as a patch key — only the
  //     entitlement-write path is allowed to stamp it. Caller passing it is
  //     a programming error; we drop the key silently rather than crash so a
  //     future caller doesn't corrupt the timestamp via patch-key ordering.
  //   * `brandAuthorityEntitled` is treated as a Story 20.7 invariant: once
  //     `existing.brandAuthorityEntitled === true`, NEVER overwrite. This
  //     enforces the JSDoc contract that was previously only documentation.
  //   * Other keys: patch wins (matches Round 1 baseline for forward-compat).
  //
  // CR Round 3 #3 — defend against clock-skew / corrupted `anchorTextProbedAt`
  // values via Number.isFinite + sanity bounds.
  const merged: Record<string, unknown> = { ...existing };
  const now = Date.now();
  const existingProbedAtRaw = existing.anchorTextProbedAt;
  const existingProbedAt =
    typeof existingProbedAtRaw === "number" &&
    Number.isFinite(existingProbedAtRaw) &&
    existingProbedAtRaw > 0 &&
    existingProbedAtRaw <= now
      ? existingProbedAtRaw
      : 0;
  const probeIsStale =
    existingProbedAt > 0 && now - existingProbedAt > MOZ_ANCHOR_TEXT_REPROBE_TTL_MS;

  let entitlementChanged = false; // whether we should stamp anchorTextProbedAt
  for (const [k, v] of Object.entries(patch)) {
    if (k === "anchorTextProbedAt") {
      // R3-1 — forbid direct patch of the timestamp; only the entitlement
      // path is allowed to stamp it. Silently drop the key.
      continue;
    }
    if (k === "anchorTextEntitled") {
      // Freshness-aware path.
      if (existing[k] === undefined || probeIsStale) {
        // Never probed OR stale → patch wins.
        merged[k] = v;
        if (existing[k] !== v) entitlementChanged = true;
      } else if (existing[k] === v) {
        // Idempotent re-probe — no-op (R3-2: do NOT restamp the timestamp).
        // Leave merged[k] === existing[k]; entitlementChanged stays false.
      } else {
        // Fresh existing differs — preserve it (race-loser path).
        merged[k] = existing[k];
      }
    } else if (k === "brandAuthorityEntitled") {
      // R3-11 — Story 20.7 invariant: once true, never overwrite. Patch may
      // upgrade undefined → boolean, but never downgrade true → false.
      if (existing[k] === true) {
        // Preserve Story 20.7 connect-time-set value.
        merged[k] = existing[k];
      } else {
        merged[k] = v;
      }
    } else {
      // Default: patch overrides existing (forward-compatible for keys not
      // explicitly defended above).
      merged[k] = v;
    }
  }

  // R3-2 — only stamp anchorTextProbedAt when the entitlement value actually
  // changed (or this is the first probe, which is also a change relative to
  // the existing `undefined`). Idempotent re-probes do NOT cause a KV write.
  if (entitlementChanged) {
    merged.anchorTextProbedAt = now;
  }

  // Skip the KV write when nothing changed (saves an op + preserves TTL).
  let changed = false;
  for (const k of Object.keys(merged)) {
    if (merged[k] !== existing[k]) {
      changed = true;
      break;
    }
  }
  if (!changed) return;

  try {
    await kv.put(key, JSON.stringify(merged));
  } catch {
    // KV write failure — non-fatal; next first-call re-probes.
  }
}

// ============================================================================
// MOZ_METRICS — 55 metrics across 5 method groups (Story 35.2)
// ============================================================================

/**
 * 55 wire-format + synthetic metrics across 5 method groups:
 *   - Site Metrics Group (23): 15 Story 20.7 base + 8 Story 35.2 expansion
 *     (3 page-identity catalog rows: pageTitle / subdomain / rootDomain +
 *     5 subdomain-rollup fields: pagesToSubdomain et al)
 *   - Brand Authority Group (1): brandAuthorityScore (V3 only, async; Story 20.7)
 *   - Top Pages Group (13): 8 Story 20.7 base + 5 Story 35.2 extended fields
 *   - Linking Root Domains Group (10): 6 Story 20.7 base + 4 Story 35.2 extended fields
 *   - Anchor Text Group (6, NEW in Story 35.2, paid-tier-gated): 3 wire-format
 *     metrics + 3 dimension-shaped catalog rows (scope / sort / next_token)
 *   - Synthetic Aggregate (2, Story 20.8): domainAuthorityTrend / daTrendDirection
 *
 * **5 _reportType discriminators** (Story 35.2 extends Story 20.7's 4):
 * `site-metrics` / `brand-authority` / `top-pages` / `linking-root-domains` /
 * `anchor-text`.
 *
 * **Every entry's `apiField` matches the snake_case Moz wire field.** A
 * build-time test in `metric-allowlist.test.ts` enforces this — typos in
 * `apiField` surface at CI time.
 *
 * **Per-method JSON-RPC call cost (Story 35.2):**
 *   - Site Metrics: 1 row per call (8 new fields piggyback on existing call)
 *   - Brand Authority: 1 + N polls per call (async)
 *   - Top Pages: N rows per call (5 new fields piggyback on existing call)
 *   - Linking Root Domains: N rows per call (4 new fields piggyback)
 *   - Anchor Text: N rows per call (NEW endpoint; paid-tier-gated 1-row 403)
 */
export const MOZ_METRICS: MetricDefinition[] = [
  // ── Site Metrics Group (15) ────────────────────────────────────────
  {
    key: "domainAuthority",
    label: "Domain Authority",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge", "line-chart"],
    apiField: "domain_authority",
    description: "The headline Moz authority metric (1-100). Predicts how well a domain ranks in Google search results.",
  },
  {
    key: "pageAuthority",
    label: "Page Authority",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "page_authority",
    description: "Predicts how well a specific page will rank (1-100).",
  },
  {
    key: "spamScore",
    label: "Spam Score",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "spam_score",
    description: "Predicts whether a domain is likely spam (0-100; null = no Moz score available).",
  },
  {
    key: "linkPropensity",
    label: "Link Propensity",
    type: "number",
    category: "site-metrics",
    dataType: "Decimal",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi"],
    apiField: "link_propensity",
    description: "Probability a randomly-selected page on this domain links externally (0-1).",
  },
  {
    key: "pagesToPage",
    label: "Inbound Pages",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "pages_to_page",
    description: "Total pages linking to this target.",
  },
  {
    key: "externalPagesToPage",
    label: "External Inbound Pages",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "external_pages_to_page",
    description: "Distinct external pages linking to this target.",
  },
  {
    key: "nofollowPagesToPage",
    label: "Nofollow Inbound Pages",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "nofollow_pages_to_page",
    description: "Inbound pages with nofollow attribute.",
  },
  {
    key: "redirectPagesToPage",
    label: "Redirect Inbound Pages",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "redirect_pages_to_page",
    description: "Inbound pages that are 30x redirects.",
  },
  {
    key: "deletedPagesToPage",
    label: "Deleted Inbound Pages",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "deleted_pages_to_page",
    description: "Previously-discovered inbound pages that returned 4xx on most recent crawl.",
  },
  {
    key: "rootDomainsToPage",
    label: "Linking Root Domains",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "root_domains_to_page",
    description: "Distinct root domains linking to this target.",
  },
  {
    key: "nofollowRootDomainsToPage",
    label: "Nofollow Linking Root Domains",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "nofollow_root_domains_to_page",
    description: "Distinct root domains linking via nofollow.",
  },
  {
    key: "pagesCrawledFromRootDomain",
    label: "Pages Crawled (Root Domain)",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "pages_crawled_from_root_domain",
    description: "Total pages Moz has crawled on this root domain.",
  },
  {
    key: "rootDomainsFromRootDomain",
    label: "Outbound Root Domains",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "root_domains_from_root_domain",
    description: "Distinct root domains this domain links to.",
  },
  {
    key: "lastCrawled",
    label: "Last Crawled",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "last_crawled",
    description: "Date Moz last crawled this target (freshness indicator).",
  },
  {
    key: "httpCode",
    label: "HTTP Code",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "http_code",
    description: "HTTP status from Moz's most recent crawl (0 = never crawled; <100 = crawl failure).",
  },

  // ── Story 35.2 — URL Metrics Expansion: Site Identity + Subdomain Rollup (8 NEW) ──
  // 3 pre-existing transformer outputs (title / subdomain / root_domain) get
  // catalogue rows here for the first time. The 5 subdomain-rollup fields
  // (pages_to_subdomain et al) are returned unconditionally by
  // data.site.metrics.fetch — ZERO new JSON-RPC call cost.
  {
    key: "pageTitle",
    label: "Page Title",
    type: "number",
    category: "site-metrics",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "title",
    description: "Page title from Moz's most recent crawl (used by Backlink Profile + Top Pages data-table widgets).",
  },
  {
    key: "subdomain",
    label: "Subdomain",
    type: "number",
    category: "site-metrics",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "subdomain",
    description: "Subdomain portion of the target URL (e.g. `blog` for blog.example.com).",
  },
  {
    key: "rootDomain",
    label: "Root Domain",
    type: "number",
    category: "site-metrics",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "root_domain",
    description: "Root domain portion of the target URL (e.g. `example.com` for blog.example.com).",
  },
  {
    key: "pagesToSubdomain",
    label: "Inbound Pages to Subdomain",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "pages_to_subdomain",
    description: "Total pages linking to any URL on this subdomain (subdomain-rollup of pagesToPage). Cross-tool widget (Story 20.9) treats this as the Moz analogue of Ahrefs liveBacklinks + Semrush backlinks when the target is a subdomain.",
  },
  {
    key: "nofollowPagesToSubdomain",
    label: "Nofollow Inbound Pages to Subdomain",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "nofollow_pages_to_subdomain",
    description: "Inbound pages to the subdomain carrying the nofollow attribute.",
  },
  {
    key: "redirectPagesToSubdomain",
    label: "Redirect Inbound Pages to Subdomain",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "redirect_pages_to_subdomain",
    description: "Inbound pages to the subdomain that are 30x redirects.",
  },
  {
    key: "externalPagesToSubdomain",
    label: "External Inbound Pages to Subdomain",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "external_pages_to_subdomain",
    description: "Distinct external pages linking to any URL on this subdomain.",
  },
  {
    key: "rootDomainsToSubdomain",
    label: "Linking Root Domains to Subdomain",
    type: "number",
    category: "site-metrics",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi", "line-chart"],
    apiField: "root_domains_to_subdomain",
    description: "Unique root domains linking to any URL on this subdomain.",
  },

  // ── Brand Authority Group (1) ──────────────────────────────────────
  {
    key: "brandAuthorityScore",
    label: "Brand Authority",
    type: "number",
    category: "brand-authority",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "gauge"],
    apiField: "brand_authority_score",
    description: "Moz's 2026-flagship brand-recognition signal (0-100, V3 ONLY, async). Requires Moz API ≥ $20/mo plan.",
  },

  // ── Top Pages Group (8) ────────────────────────────────────────────
  {
    key: "topPagePageUrl",
    label: "Top Page URL",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    // Story 31-31 Code Review: URL string (pre-existing "Integer" mislabel) —
    // identity fact, no aggregationKind; in NON_NUMERIC_FACT_EXEMPTIONS.
    compatibleWidgets: ["data-table"],
    apiField: "page",
    description: "URL of the top-performing page.",
  },
  {
    key: "topPagePageAuthority",
    label: "Top Page PA",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table"],
    apiField: "page_authority",
    description: "Page Authority of the top-page row.",
  },
  {
    key: "topPagePagesToPage",
    label: "Top Page Inbound Pages",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "pages_to_page",
    description: "Inbound page count for the top-page row.",
  },
  {
    key: "topPageExternalPagesToPage",
    label: "Top Page External Inbound",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "external_pages_to_page",
    description: "External inbound page count for the top-page row.",
  },
  {
    key: "topPageRootDomainsToPage",
    label: "Top Page Linking Root Domains",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "root_domains_to_page",
    description: "Linking root domain count for the top-page row.",
  },
  {
    key: "topPageTitle",
    label: "Top Page Title",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "title",
    description: "Page title from Moz's most recent crawl.",
  },
  {
    key: "topPageHttpCode",
    label: "Top Page HTTP Code",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "http_code",
    description: "HTTP status of the top-page row.",
  },
  {
    key: "topPageLastCrawled",
    label: "Top Page Last Crawled",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "last_crawled",
    description: "Last-crawled date for the top-page row.",
  },

  // ── Story 35.2 — Top Pages Extended (5 NEW) ────────────────────────
  // All 5 fields are returned unconditionally by data.site.top-pages.list per
  // moz.com/help/links-api/making-calls/top-pages verified 2026-05-19/2026-05-20.
  // ZERO new JSON-RPC call cost — only catalogue + transformer extensions.
  {
    key: "topPageNofollowPagesToPage",
    label: "Top Page Nofollow Inbound",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "nofollow_pages_to_page",
    description: "Inbound pages with nofollow attribute for this top-page row.",
  },
  {
    key: "topPageRedirectPagesToPage",
    label: "Top Page Redirect Inbound",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "redirect_pages_to_page",
    description: "Inbound pages that are 30x redirects for this top-page row.",
  },
  {
    key: "topPageDeletedPagesToPage",
    label: "Top Page Deleted Inbound",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "deleted_pages_to_page",
    description: "Inbound pages that have been deleted since Moz last saw them.",
  },
  {
    key: "topPageExternalNofollowPagesToPage",
    label: "Top Page External Nofollow Inbound",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "external_nofollow_pages_to_page",
    description: "External inbound pages with nofollow attribute for this top-page row.",
  },
  {
    key: "topPageIndirectRootDomainsToPage",
    label: "Top Page Indirect Linking Root Domains",
    type: "number",
    category: "top-pages",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "indirect_root_domains_to_page",
    description: "Root domains linking to a page that itself redirects to this top-page row.",
  },

  // ── Linking Root Domains Group (6) ─────────────────────────────────
  {
    key: "linkingRootDomain",
    label: "Linking Root Domain",
    type: "number",
    category: "linking-root-domains",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table"],
    apiField: "linking_root_domain",
    description: "The referring root domain.",
  },
  {
    key: "sourceDomainAuthority",
    label: "Source DA",
    type: "number",
    category: "linking-root-domains",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table", "kpi"],
    apiField: "source_domain_authority",
    description: "Domain Authority of the referring root domain.",
  },
  {
    key: "linkerLinkPropensity",
    label: "Linker Link Propensity",
    type: "number",
    category: "linking-root-domains",
    dataType: "Decimal",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table"],
    apiField: "link_propensity",
    description: "Link propensity of the referring root domain.",
  },
  {
    key: "linkerSpamScore",
    label: "Linker Spam Score",
    type: "number",
    category: "linking-root-domains",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table"],
    apiField: "spam_score",
    description: "Spam Score of the referring root domain (null = no Moz score).",
  },
  {
    key: "nofollowPagesPerLinker",
    label: "Linker Nofollow Pages",
    type: "number",
    category: "linking-root-domains",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table"],
    apiField: "nofollow_pages",
    description: "Nofollow inbound pages from the referring root domain.",
  },
  {
    key: "redirectPagesPerLinker",
    label: "Linker Redirect Pages",
    type: "number",
    category: "linking-root-domains",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table"],
    apiField: "redirect_pages",
    description: "Redirect inbound pages from the referring root domain.",
  },

  // ── Story 35.2 — Linking Root Domains Extended (4 NEW) ─────────────
  // Returned unconditionally by data.site.linking-root-domains.list per
  // moz.com/help/links-api/making-calls/linking-root-domains verified
  // 2026-05-19/2026-05-20. ZERO new JSON-RPC call cost.
  {
    key: "deletedPagesPerLinker",
    label: "Linker Deleted Pages",
    type: "number",
    category: "linking-root-domains",
    dataType: "Integer",
    aggregationKind: "ratio",
    compatibleWidgets: ["data-table"],
    apiField: "deleted_pages",
    description: "Pages on the referring root domain that have been deleted since Moz last crawled them.",
  },
  {
    key: "linkerLastCrawled",
    label: "Linker Last Crawled",
    type: "number",
    category: "linking-root-domains",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "last_crawled",
    description: "Date Moz last crawled the referring root domain (ISO YYYY-MM-DD).",
  },
  {
    key: "linkerFirstSeen",
    label: "Linker First Seen",
    type: "number",
    category: "linking-root-domains",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "first_seen",
    description: "Date Moz first discovered the link from the referring root domain (ISO YYYY-MM-DD).",
  },
  {
    key: "linkerLinkPattern",
    label: "Linker Link Pattern",
    type: "number",
    category: "linking-root-domains",
    dataType: "Enum",
    compatibleWidgets: ["data-table"],
    apiField: "link_pattern",
    description: "Categorical pattern of the link (e.g. `text`, `image`, `redirect`).",
  },

  // ── Story 35.2 — Anchor Text Group (6 NEW, PAID-TIER GATED) ────────
  // 5th _reportType discriminator added by Story 35.2. The 3 wire-format
  // entries (anchorTextTerm / anchorTextExternalPages / anchorTextExternalRootDomains)
  // are widget-bound. The 3 dimension-shaped entries (anchorTextScope /
  // anchorTextSortField / anchorTextNextToken) surface only as widget-config
  // picker dimensions — they carry empty `compatibleWidgets: []` so the
  // renderer does NEVER tries to bind them to a data-shape.
  //
  // **CR Round 1 #5 (HIGH) — type schema constraint.** `MetricDefinition.type`
  // allows only `"number" | "percentage" | "duration" | "currency"` per
  // adapter.ts:247. The 3 dimension-shaped + anchorTextTerm rows would
  // semantically want `"string"` but the schema does NOT allow it — so we use
  // `type: "number"` (existing codebase convention; e.g. `topPageTitle`,
  // `topPageLastCrawled`) + `dataType: "Enum"` (the correct string-shaped
  // dataType). The `compatibleWidgets: []` invariant on the 3 dimension rows
  // is the load-bearing protection — any future widget that tries to bind to
  // these keys MUST type-check `compatibleWidgets.length > 0` before invoking
  // a renderer. Document this here so a future agent doesn't naively change
  // the dataType to "Integer" thinking it's a typo.
  //
  // Entitlement: lazy first-call probe (DIVERGENT from Story 20.7 Brand
  // Authority's eager-at-connect probe). See orchestrator dispatch in
  // fetchMetrics + AC #5 / AC #6 for the entitlement-skip + persist semantics.
  {
    key: "anchorTextTerm",
    label: "Anchor Text",
    type: "number",
    category: "anchor-text",
    dataType: "Enum",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "anchor_text",
    description: "The anchor text string used by external pages linking to the target. Moz-EXCLUSIVE in the cross-tool widget (Ahrefs/Semrush surface anchor-text in their UI but not in their public APIs as of 2026-05-19).",
  },
  {
    key: "anchorTextExternalPages",
    label: "Anchor External Pages",
    type: "number",
    category: "anchor-text",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "external_pages",
    description: "Unique external pages linking to the target using this anchor text.",
  },
  {
    key: "anchorTextExternalRootDomains",
    label: "Anchor External Root Domains",
    type: "number",
    category: "anchor-text",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["data-table", "bar-chart"],
    apiField: "external_root_domains",
    description: "Unique external root domains linking to the target using this anchor text.",
  },
  // CR Round 2 #4 — the 3 dimension-shaped anchor-text rows are
  // request-time parameters echoed into records (NOT wire-format metric
  // VALUES). Marked `synthetic: true` so the widget-config UI hides them
  // from per-endpoint metric selectors per the contract documented at
  // adapter.ts:308-317. The `compatibleWidgets: []` invariant is the
  // secondary defense; `synthetic` is the primary.
  {
    key: "anchorTextScope",
    label: "Anchor Scope",
    type: "number",
    category: "anchor-text",
    dataType: "Enum",
    compatibleWidgets: [],
    synthetic: true,
    apiField: "scope",
    description: "Request-time scope (page / subdomain / root_domain) — surfaces as a widget-config dimension picker option only.",
  },
  {
    key: "anchorTextSortField",
    label: "Anchor Sort Field",
    type: "number",
    category: "anchor-text",
    dataType: "Enum",
    compatibleWidgets: [],
    synthetic: true,
    apiField: "sort",
    description: "Request-time sort literal (domain_authority / pages_linking / page_authority) — widget-config dimension picker option only.",
  },
  {
    key: "anchorTextNextToken",
    label: "Anchor Pagination Cursor",
    type: "number",
    category: "anchor-text",
    dataType: "Enum",
    compatibleWidgets: [],
    synthetic: true,
    apiField: "next_token",
    description: "Opaque pagination cursor for fetching subsequent pages of anchor-text rows (Moz V3 cursor-based pagination).",
  },

  // ── Story 20.8 — Synthetic Aggregate Metrics (2) ──────────────────────
  // These 2 metrics are SYNTHETIC AGGREGATES — they do NOT correspond to
  // wire-format Moz API fields. Moz V3 has no time-series surface; both
  // metrics are computed at preset-expansion time from the
  // `moz_authority_snapshots` D1 table that Story 20.7 shipped.
  //
  // `apiField` is stamped `_synthetic_snapshot_query` so the metric-allowlist
  // build-time test exempts them from the per-category wire-field allowlist
  // check via `MOZ_SYNTHETIC_METRIC_KEYS`. The widget-config UI hides these
  // from per-endpoint selectors but exposes them via preset expansions only.
  // Mirrors Semrush Story 20.6 AC #12 `aiOverviewPresenceRate` pattern.
  {
    key: "domainAuthorityTrend",
    label: "Domain Authority Trend (6-month)",
    type: "number",
    category: "snapshot-trend",
    dataType: "Array",
    compatibleWidgets: ["line-chart"],
    apiField: "_synthetic_snapshot_query",
    description:
      "Synthetic — 6 monthly snapshots of Domain Authority from the moz_authority_snapshots D1 table (Story 20.7 build-to-sell). Moz V3 has NO time-series API; this metric is impossible without ProvenLeap's snapshot persistence layer.",
  },
  {
    key: "daTrendDirection",
    label: "DA Trend Direction (6-month)",
    type: "number",
    category: "snapshot-trend",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: ["kpi"],
    apiField: "_synthetic_snapshot_query",
    description:
      "Synthetic — signed integer = currentDA - oldestDA over the 6-month snapshot window. ▲ +3 over 6 months for GreenLife (Dec 2025 DA=47 → May 2026 DA=50).",
  },
];

/**
 * Story 20.8 — Set of synthetic-aggregate metric keys whose `apiField` is the
 * `_synthetic_snapshot_query` sentinel. The metric-allowlist build-time test
 * exempts these from the per-category wire-field allowlist check. Mirrors
 * the Semrush 20.6 `SEMRUSH_SYNTHETIC_METRIC_KEYS` pattern.
 *
 * **DO NOT add a metric here to "fix" a test failure.** This Set is reserved
 * for metrics that are genuinely computed at preset-expand time from D1
 * snapshot data or aggregate reducers, not from wire-format API fields.
 */
// Code Review Pass 2 BH2-1 — `Object.freeze` does NOT prevent `.add()` /
// `.delete()` / `.clear()` on a Set (those operate on internal slots, not
// properties). The Pass 1 BH-18 claim was factually wrong. Replaced with a
// `Proxy` that throws on any mutation method call — runtime-enforced
// immutability for the synthetic-metric allowlist.
const MOZ_SYNTHETIC_METRIC_KEYS_INTERNAL = new Set<string>([
  "domainAuthorityTrend",
  "daTrendDirection",
]);

const SET_MUTATION_METHODS = new Set(["add", "delete", "clear"]);

/**
 * Read-only Set proxy. Forwards reads to the underlying Set but throws on
 * `add`/`delete`/`clear` calls. Mirrors the documented `SEMRUSH_SYNTHETIC_METRIC_KEYS`
 * invariant ("DO NOT add a metric here to 'fix' a test failure").
 *
 * Implementation note: `Set.prototype.size` is a getter (NOT a method),
 * so the Proxy `get` trap must forward it WITHOUT receiver-binding —
 * otherwise V8 throws "Method get Set.prototype.size called on incompatible
 * receiver". Methods like `has` / `keys` / `values` / `entries` /
 * `[Symbol.iterator]` are bound to the underlying Set so their internal
 * slot reads succeed.
 *
 * Code Review Pass 3 BH3-5 — `forEach` gets special handling: the native
 * `Set.prototype.forEach(callback, thisArg)` invokes `callback(value, value, set)`
 * where `set` is the underlying Set — which would leak a mutable handle.
 * We intercept `forEach` and substitute the proxy itself as the third arg
 * so callers cannot escape the readonly contract through the `set` parameter.
 */
function asReadonlySet<T>(source: Set<T>): ReadonlySet<T> {
  let proxyRef: ReadonlySet<T>;
  const proxy = new Proxy(source, {
    get(target, prop, _receiver) {
      if (typeof prop === "string" && SET_MUTATION_METHODS.has(prop)) {
        return () => {
          throw new TypeError(
            `Cannot mutate MOZ_SYNTHETIC_METRIC_KEYS — Set is read-only at runtime per build-to-sell-directive. Use MOZ_METRICS expansions instead.`,
          );
        };
      }
      // Pass 3 BH3-5 — special-case `forEach` to substitute the proxy
      // (not the underlying Set) as the third callback argument.
      if (prop === "forEach") {
        return (
          callback: (value: T, value2: T, set: ReadonlySet<T>) => void,
          thisArg?: unknown,
        ): void => {
          target.forEach((v) => {
            callback.call(thisArg, v, v, proxyRef);
          });
        };
      }
      // Read directly from the underlying Set object (NOT the proxy
      // receiver) so getter properties like `size` and methods that access
      // internal slots work correctly.
      const value = Reflect.get(target, prop, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
  }) as ReadonlySet<T>;
  proxyRef = proxy;
  return proxy;
}

export const MOZ_SYNTHETIC_METRIC_KEYS: ReadonlySet<string> = asReadonlySet(
  MOZ_SYNTHETIC_METRIC_KEYS_INTERNAL,
);

// ============================================================================
// MOZ_DIMENSIONS — 5 dimensions (AC #13)
// ============================================================================

export const MOZ_DIMENSIONS: DimensionDefinition[] = [
  {
    key: "target",
    label: "Target",
    type: "Content",
    exampleValues: ["example.com", "subdomain.example.com"],
  },
  {
    key: "scope",
    label: "Scope",
    type: "Geo",
    exampleValues: ["page", "subdomain", "root_domain"],
  },
  {
    key: "indexId",
    label: "Index Snapshot",
    type: "Time",
    exampleValues: ["index_2026_05", "index_2026_04"],
  },
  {
    key: "linkTypeFilter",
    label: "Link Type",
    type: "Tech",
    exampleValues: ["external", "nofollow", "external+nofollow"],
  },
  {
    key: "sortField",
    label: "Sort Field",
    type: "Search",
    exampleValues: [
      "page_authority",
      "domain_authority",
      "root_domains_to_page",
      "source_domain_authority",
    ],
  },
];

// ============================================================================
// Story 20.8 — Fixture-mode loader (AC #9, #15, #21)
// ============================================================================

/**
 * Moz fixture-mode helper (Story 20.8 — replaces the Story 20.7 stub at this
 * file's former `fixture_pending_story_20_8` warning).
 *
 * Mirrors `loadSemrushFixture` from Story 20.6 verbatim with three Moz-specific
 * divergences:
 *
 * 1. **No Trends-API gate.** Moz has no equivalent entitlement-by-route layer.
 *    Instead, Moz has the Brand Authority entitlement which is forced TRUE
 *    in fixture mode (Round 4 ADR-D) — the BA Gauge widget demos with the
 *    populated state regardless of any KV-stored metadata.
 *
 * 2. **No `defaultDatabase`.** Moz is global, not per-database.
 *
 * 3. **`dataFreshnessLagHours: 24` invariant.** Mirrors Semrush's at-most-daily
 *    crawl cadence, but with Moz Index monthly refresh semantics. The fixture
 *    helper force-overwrites this field per the Story 20.7 cache-TTL
 *    convention.
 *
 * **No new Moz network calls** (Round 4 QA invariant — fixture-mode-vs-
 * production-mode separation). `safeFetchMoz` / `fetchMozSiteMetrics` /
 * `fetchMozBrandAuthority` / `fetchMozTopPages` / `fetchMozLinkingRootDomains` /
 * `mozJsonRpcCall` are NEVER called.
 *
 * **No production cache writes** (Round 3 Red Team Attack #3 — production-
 * cache-write-prevention). The helper reads ONLY `fixture:moz:{slug}` keys
 * and NEVER writes to ANY KV key. The production cache prefix `moz:*` is
 * not touched. `persistMozSnapshotFireAndForget` is also never called — the
 * 6 demo-bootstrap snapshots from AC #8 are seeded SEPARATELY at workspace
 * bootstrap, not from fetchMetrics.
 *
 * **Brand Authority entitlement bypass** (Round 4 ADR-D — Story 20.8 NEW
 * invariant): the fixture-mode helper does NOT read the
 * `kv.get("moz:meta:{integrationId}")` side-channel for `brandAuthorityEntitled`.
 * Instead, it forces `metadata.brandAuthorityEntitled = true`
 * UNCONDITIONALLY in fixture mode (the GreenLife fixture demos with the
 * BA-score-populated state regardless of any KV-stored metadata).
 *
 * **Force-overwrite `dataFreshnessLagHours: 24`** (Story 20.7 cache-TTL
 * convention): even if the fixture stamps a different value, the helper
 * force-overwrites this field per Moz stamping convention.
 *
 * **Cross-tenant fixture-data leak protection** (Round 3 Red Team Attack #2):
 * the helper resolves `clientSlug` via `getClientSlug(db, ctx.tenantId,
 * ctx.clientId)` — the SQL-layer (tenantId, clientId) scope enforces the
 * isolation. Never bypass the resolver.
 *
 * **Structured-log telemetry per AC #21** — emits one of
 * `moz_demo_fixture_loaded` / `_missing` / `_binding_missing` per invocation
 * so the support team can debug missing fixtures from observability tools.
 */

async function loadMozFixture(
  ctx: AdapterContext,
  dateRange: { start: Date; end: Date },
): Promise<AdapterResult<AdapterMetrics>> {
  const env = ctx.env as unknown as Record<string, unknown>;
  const kv = env.KV as unknown;
  const db = env.DB as unknown;

  // AC #21 — all 3 telemetry events use console.log per spec verbatim.
  if (!kv || typeof (kv as { get?: unknown }).get !== "function") {
    console.log(
      JSON.stringify({
        event: "moz_demo_fixture_binding_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        missingBinding: "kv",
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing KV binding — cannot load Moz demo fixture",
      retryable: false,
    });
  }
  if (!db) {
    console.log(
      JSON.stringify({
        event: "moz_demo_fixture_binding_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        missingBinding: "db",
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "permission_denied",
      message: "Missing DB binding — cannot resolve Moz demo client slug",
      retryable: false,
    });
  }

  // Defensive validation that ctx.tenantId + ctx.clientId are non-empty
  // strings BEFORE calling getClientSlug. Round 3 Red Team Attack #2
  // cross-tenant leak protection: empty strings could match the first row in
  // the table depending on prepared-statement semantics.
  if (
    typeof ctx.tenantId !== "string" ||
    ctx.tenantId.length === 0 ||
    typeof ctx.clientId !== "string" ||
    ctx.clientId.length === 0
  ) {
    return adapterErr({
      code: "permission_denied",
      message:
        "Moz demo fixture: ctx.tenantId and ctx.clientId must be non-empty strings",
      retryable: false,
    });
  }

  // Validate date range — reversed ranges silently return zero records and
  // Invalid Date crashes via toISOString. Return adapterErr for a typed
  // AdapterResult instead. (Mirrors Semrush 20.6 + Ahrefs 20.3 finding.)
  const startTime = dateRange.start?.getTime?.();
  const endTime = dateRange.end?.getTime?.();
  if (
    typeof startTime !== "number" ||
    !Number.isFinite(startTime) ||
    typeof endTime !== "number" ||
    !Number.isFinite(endTime)
  ) {
    return adapterErr({
      code: "invalid_request",
      message:
        "Moz demo fixture: dateRange.start and dateRange.end must be finite Date instances",
      retryable: false,
    });
  }
  if (startTime > endTime) {
    return adapterErr({
      code: "invalid_request",
      message:
        "Moz demo fixture: dateRange.start must be on or before dateRange.end",
      retryable: false,
    });
  }

  const { getFixtureFromKV, filterFixtureByDateRange } = await import(
    "../fixtures/kv-loader"
  );
  const { getClientSlug } = await import("../fixtures/demo-fetch");

  let clientSlug: string;
  try {
    // AC #15 / Round 3 Red Team Attack #2 — `getClientSlug` enforces the
    // `(tenantId, clientId)` scope at the SQL query layer. We MUST pass
    // both. Bypassing this resolver would silently leak fixture data across
    // tenants.
    clientSlug = await getClientSlug(
      db as Parameters<typeof getClientSlug>[0],
      ctx.tenantId,
      ctx.clientId,
    );
  } catch (err) {
    return adapterErr({
      code: "account_not_found",
      message: `Moz demo client not found: ${err instanceof Error ? err.message : String(err)}`,
      retryable: false,
    });
  }

  const startedAt = Date.now();
  const metrics = await getFixtureFromKV(
    kv as Parameters<typeof getFixtureFromKV>[0],
    "moz",
    clientSlug,
  );

  if (metrics === null) {
    console.log(
      JSON.stringify({
        event: "moz_demo_fixture_missing",
        tenantId: ctx.tenantId,
        clientId: ctx.clientId,
        integrationId: ctx.integrationId,
        clientSlug,
        kvKey: `fixture:moz:${clientSlug}`,
        timestamp: new Date().toISOString(),
      }),
    );
    return adapterErr({
      code: "account_not_found",
      message: `Moz demo fixture not yet uploaded for client ${clientSlug}; run pnpm fixtures:upload`,
      retryable: false,
    });
  }

  const filtered = filterFixtureByDateRange(
    metrics,
    dateRange.start,
    dateRange.end,
  );

  // Warn when the date-range filter strips the fixture down to zero records
  // (carry-forward from Semrush 20.6 Pass 1 Blind Hunter #8 — otherwise the
  // UI silently renders empty widgets with no diagnostic).
  const filteredWarnings = [...(filtered.warnings ?? [])];
  if (filtered.records.length === 0) {
    filteredWarnings.push({
      reportType: "*",
      reason: `Moz demo fixture filtered to zero records for date range ${dateRange.start.toISOString().split("T")[0]} → ${dateRange.end.toISOString().split("T")[0]}. Try a wider window or check the fixture upload.`,
    });
  }

  // Force-overwrite `dataFreshnessLagHours` per Story 20.7 cache-TTL
  // convention (Moz Index refreshes monthly). Force-overwrite
  // `brandAuthorityEntitled = true` per AC #9 step 10 (Round 4 ADR-D
  // BA-entitlement bypass).
  //
  // `dataFinalizedThrough` is the MIN of two candidates — `Date.now() - 24h`
  // and `dateRange.end - 24h` — so the demo can't claim "data is up to
  // yesterday" when the user narrowed the date range to a back-window that
  // excludes the latest fixture rows.
  //
  // Code Review Pass 2 BH2-11 / EH2-2 — when `dateRange.end` is
  // pathologically near unix epoch, the `Math.max(0, ...)` clamp from Pass 1
  // produced a misleading `1970-01-01T00:00:00.000Z`. Better: clamp the
  // floor to `finalizedFromNow` (always reasonable since `Date.now()` is
  // wall-clock) so the worst-case output is "data finalized 24 h ago"
  // rather than "data finalized in 1970".
  const lagMs = MOZ_FIXTURE_LAG_HOURS * 60 * 60 * 1000;
  const finalizedFromNow = Date.now() - lagMs;
  const finalizedFromRange = dateRange.end.getTime() - lagMs;
  // Normal path: MIN(now, range) — covers the legitimate "user narrowed
  // dateRange.end back" case. Pathological path: if `finalizedFromRange`
  // is below a sane floor (e.g., pre-2020), fall back to `finalizedFromNow`.
  //
  // Code Review Pass 3 AA3-1 — Pass 2's `> 0` guard caught dateRange.end <
  // 1970-01-02 but still emitted 1970-01-02-era stamps for any value in
  // (0, sane-floor). The renderer treats `dataFinalizedThrough` as a
  // user-facing timestamp ("data finalized N hours ago"); emitting a 1970
  // stamp for ANY pathological dateRange.end is misleading. We floor at
  // 2020-01-01 (well-before any realistic Moz integration onboarding).
  const SANE_FINALIZED_FLOOR_MS = Date.UTC(2020, 0, 1);
  const dataFinalizedThroughMs =
    finalizedFromRange >= SANE_FINALIZED_FLOOR_MS
      ? Math.min(finalizedFromNow, finalizedFromRange)
      : finalizedFromNow;
  const dataFinalizedThrough = new Date(dataFinalizedThroughMs).toISOString();
  const result: AdapterMetrics = {
    ...filtered,
    warnings: filteredWarnings,
    metadata: {
      ...(filtered.metadata ?? {}),
      dataFreshnessLagHours: MOZ_FIXTURE_LAG_HOURS,
      dataFinalizedThrough,
      brandAuthorityEntitled: true,
    },
  };

  console.log(
    JSON.stringify({
      event: "moz_demo_fixture_loaded",
      tenantId: ctx.tenantId,
      clientId: ctx.clientId,
      integrationId: ctx.integrationId,
      clientSlug,
      recordCount: result.recordCount,
      durationMs: Date.now() - startedAt,
      timestamp: new Date().toISOString(),
    }),
  );

  return adapterOk(result);
}

// ============================================================================
// Adapter implementation (AC #4, #6, #8, #14, #16)
// ============================================================================

export const mozAdapter: IntegrationAdapter = {
  platform: PLATFORM,
  authType: "api-key",
  rateLimits: {
    // Per moz.com/community/q/post/335766 verified 2026-05-10:
    //   - Free tier: 1 req / 10 sec = 6/min raw
    //   - Paid tiers: 50 req / 5 min sliding window = 10/min steady state
    // We declare 10/min as the conservative ceiling — paid customers get the
    // speed; free-tier customers get throttled by the queue's built-in
    // backpressure. burstLimit: 1 prevents thundering-herd across queue
    // consumers; daily 14_400 = 10/min × 60 × 24 is theoretical max.
    requestsPerMinute: 10,
    requestsPerDay: 14_400,
    burstLimit: 1,
  },

  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<boolean>> {
    const apiToken = credentials.accessToken;
    if (!apiToken || apiToken.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Moz API token is missing or empty.",
        retryable: false,
      });
    }
    try {
      const result = await validateMozConnection(apiToken, ctx.env);
      // **Round 1+2 review fix (AC #6):** the spec requires the cron
      // healthCheck to emit `moz_row_balance_zero` notification when
      // `quota.remaining === 0`. Round 2 fix: dedup via KV marker keyed by
      // (integrationId + UTC date) — emit at most ONCE PER DAY per integration
      // instead of every cron tick (cron fires every 15 min × 96 ticks/day,
      // which would have spammed Datadog/Sentry continuously for any tenant
      // on a depleted plan).
      if (result.rowsRemaining === 0) {
        const kv = getAdapterKv(ctx);
        const today = todayUtcYyyyMmDd();
        const markerKey = `moz:row_balance_zero_emitted:${ctx.integrationId}:${today}`;
        let shouldEmit = true;
        if (kv) {
          try {
            const marker = await kv.get(markerKey);
            if (marker !== null) {
              shouldEmit = false;
            } else {
              await kv.put(markerKey, String(Date.now()), {
                expirationTtl: 25 * 3600,
              });
            }
          } catch {
            // KV failure means we may double-emit once — acceptable.
          }
        }
        if (shouldEmit) {
          emitStructuredLog("moz_row_balance_zero", {
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            rowsLimit: result.rowsLimit,
            planTier: result.planTier,
          });
        }
      }
      return adapterOk(true);
    } catch (err) {
      const status = (err as { status?: number }).status ?? 0;
      const code: "token_expired" | "permission_denied" | "provider_unavailable" =
        status === 401
          ? "token_expired"
          : status === 403
            ? "permission_denied"
            : "provider_unavailable";
      return adapterErr({
        code,
        message: sanitizeMozErrorMessage(
          err instanceof Error ? err.message : String(err),
          apiToken,
        ),
        retryable: code === "provider_unavailable",
      });
    }
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date },
  ): Promise<AdapterResult<AdapterMetrics>> {
    // Story 20.8 — fixture-mode short-circuit (AC #9). MUST run BEFORE any
    // production-path validation (target / apiToken / metadata side-channel /
    // row-budget) so a demo workspace with no live Moz + API token still
    // renders fixture data without triggering token_invalid /
    // account_not_found / row_budget_exceeded errors.
    const fixturesEnabled =
      (ctx as unknown as { fixtures?: boolean }).fixtures === true;
    if (fixturesEnabled) {
      return loadMozFixture(ctx, dateRange);
    }

    const target = ctx.accountId;
    if (!target) {
      return adapterErr({
        code: "account_not_found",
        message:
          "No Moz target configured for this integration. Set the target via the integration settings.",
        retryable: false,
      });
    }
    const apiToken = credentials.accessToken;
    if (!apiToken || apiToken.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Moz API token is missing or empty.",
        retryable: false,
      });
    }

    // Read metadata side-channel — the route handler attaches `defaultScope`
    // + `brandAuthorityEntitled` to ctx via a structural cast. If the caller
    // didn't propagate metadata (test paths, generic dispatchers), fall back
    // to the KV side-channel `moz:meta:{integrationId}`. Bounded single-get,
    // no API call.
    const kv = getAdapterKv(ctx);
    const ctxMetadata =
      (ctx as unknown as { metadata?: Record<string, unknown> }).metadata ?? {};
    let metadata: Record<string, unknown> = ctxMetadata;
    if (
      ctxMetadata.brandAuthorityEntitled === undefined &&
      kv &&
      ctx.integrationId
    ) {
      try {
        const raw = await kv.get(`moz:meta:${ctx.integrationId}`);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            if (
              parsed &&
              typeof parsed === "object" &&
              !Array.isArray(parsed)
            ) {
              metadata = { ...ctxMetadata, ...(parsed as Record<string, unknown>) };
            }
          } catch {
            // Malformed JSON — keep ctxMetadata defaults.
          }
        }
      } catch {
        // KV read failure must NOT block fetchMetrics.
      }
    }
    const scopeRaw = metadata.defaultScope;
    const scope: MozScope = isMozScope(scopeRaw) ? scopeRaw : "root_domain";
    const brandAuthorityEntitled = metadata.brandAuthorityEntitled === true;
    // Story 35.2 — Anchor Text entitlement. `undefined` = never probed
    // (first-call probe path); `true` = entitled; `false` = subscription_required
    // (skip the row spend entirely). Mirrors brandAuthorityEntitled but with
    // lazy-first-call semantics rather than eager-at-connect.
    const anchorTextEntitlementRaw = metadata.anchorTextEntitled;
    const anchorTextEntitlementKnown =
      typeof anchorTextEntitlementRaw === "boolean";
    const anchorTextEntitled =
      anchorTextEntitlementKnown && anchorTextEntitlementRaw === true;
    const anchorTextEntitledIsUnknown = !anchorTextEntitlementKnown;

    // Per-tenant row-budget circuit-breaker (AC #8). Mirrors Semrush 20.4
    // tenant-unit budget; Moz uses rows-not-units terminology.
    // **Round 1 review fix (UTC midnight rollover race):** compute the YYYY-MM-DD
    // bucket ONCE at the top of fetchMetrics and thread it through both the
    // budget check AND the increment. Otherwise a fetchMetrics call straddling
    // midnight UTC reads day N's bucket on check but writes to day N+1's bucket
    // on increment — silently poisoning day N+1 with rows that weren't actually
    // consumed during day N+1.
    const todayUtc = todayUtcYyyyMmDd();

    // CR Round 2 #7 fix — capture `budget.current` from the upfront
    // checkMozRowBudget result so the per-method projection block below
    // reuses it instead of issuing a second KV read.
    //
    // CR Round 3 #4 fix — emit an observability event when KV is unavailable
    // so the production budget-protection gap is visible in logs rather than
    // silent. We accept dispatching without budget protection in that case
    // because (a) production has KV bound, (b) test paths set kv=undefined
    // intentionally, and (c) refusing to dispatch breaks the test suite +
    // any misconfigured dev environment for no real safety benefit.
    let currentBudgetRows: number | null = null;
    if (!kv) {
      emitStructuredLog("moz_budget_check_skipped_no_kv", {
        tenantId: ctx.tenantId,
        integrationId: ctx.integrationId,
      });
    }
    if (kv) {
      const budget = await checkMozRowBudget(
        kv,
        ctx.tenantId,
        MOZ_DAILY_TENANT_ROW_BUDGET,
        todayUtc,
      );
      currentBudgetRows = budget.current;
      if (budget.exceeded) {
        if (budget.firstBreach) {
          emitStructuredLog("moz_daily_row_budget_exceeded", {
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            currentRows: budget.current,
            limitRows: budget.limit,
          });
        }
        return adapterErr({
          code: "rate_limited",
          message:
            `Daily Moz row budget exceeded (${budget.current}/${budget.limit}). ` +
            `Sync paused until UTC midnight to protect your monthly quota.`,
          retryable: false,
        });
      }
    }

    const records: Array<Record<string, unknown>> = [];
    const warnings: Array<{ reportType: string; reason: string }> = [];
    let totalRowsCharged = 0;

    // 0) Fetch index metadata FIRST — we stamp indexId on every record so the
    // snapshot persistence layer can group by index period. This is a
    // mandatory pre-call; cache hits keep cost minimal (1h TTL).
    let indexId = "unknown";
    try {
      const indexMeta = await fetchMozIndexMetadata(
        apiToken,
        ctx.env,
        ctx.integrationId,
      );
      indexId = indexMeta.indexId;
    } catch (err) {
      // Non-fatal — emit a warning and proceed with `unknown` indexId. The
      // snapshot dedup contract still works (multiple `unknown` snapshots
      // for the same target collapse to first-write-wins).
      warnings.push({
        reportType: "metadata.indexId.fetch",
        reason: "index_metadata_fetch_failed",
      });
      emitStructuredLog("moz_index_metadata_fetch_failed", {
        tenantId: ctx.tenantId,
        integrationId: ctx.integrationId,
        error: sanitizeMozErrorMessage(
          err instanceof Error ? err.message : String(err),
          apiToken,
        ),
      });
    }

    // 1-5) Fetch the 5 method groups in parallel. Each call's `.catch`
    // returns an `adapterErr`-shaped object so a synchronous throw from one
    // helper can't reject the whole promise.all (mirrors Semrush 20.4 pattern).
    const callOpts = {
      apiToken,
      env: ctx.env,
      integrationId: ctx.integrationId,
    };

    // Story 35.2 — Anchor Text dispatch decision tree:
    //   1. `metadata.anchorTextEntitled === false` → SKIP (saves the 1-row
    //      403 cost per Story 20.7 Round 4 ADR precedent).
    //   2. `metadata.anchorTextEntitled === undefined` → PROBE (first-call;
    //      flips metadata to true/false based on outcome).
    //   3. `metadata.anchorTextEntitled === true` → DISPATCH normally.
    // The `false` short-circuit emits a single `subscription_required` warning
    // below; no JSON-RPC call issued. The `undefined` + `true` branches both
    // dispatch — the response post-processor below differentiates the persist.
    //
    // Story 35.2 AC #12 (CR Round 1 #2 + Round 2 #2/#6/#7 + Round 3 #6 fix)
    // — priority-ordered per-method budget enforcement with graceful
    // degradation.
    //
    // Strategy: project the worst-case cost of each method, then DROP THE
    // LOWEST-PRIORITY METHODS one at a time until the projection fits the
    // remaining budget. Priority order (highest first → kept the longest):
    //
    //   1. site-metrics (1 row) — flagship; cheapest; never skipped
    //   2. brand-authority (1 + up to MOZ_MAX_BA_POLLS rows; flagship signal)
    //   3. top-pages (MOZ_AUTONOMOUS_LIST_LIMIT rows; most-used dashboard widget)
    //   4. linking-root-domains (MOZ_AUTONOMOUS_LIST_LIMIT rows; SEO context)
    //   5. anchor-text (MOZ_AUTONOMOUS_LIST_LIMIT rows; paid-tier supplementary)
    //
    // CR Round 3 #6 — Round 2's "skip all 3 list methods together" was a
    // sledgehammer that broke top-pages + linking-root-domains widgets all
    // day for any tenant near quota. The new approach lets the customer's
    // most-valuable widgets keep working as long as the budget allows.
    //
    // CR Round 2 #6 — `MOZ_MAX_BA_POLLS = 15` pins the upper bound for BA
    // polling (1s/2s/4s/8s/15s exponential backoff schedule × 3 retries
    // worst case at 30s = 15 polls generous ceiling).
    //
    // CR Round 2 #7 — reuses `budget.current` from upfront checkMozRowBudget.
    const MOZ_MAX_BA_POLLS = 15;
    let topPagesBudgetSkip = false;
    let linkingRootDomainsBudgetSkip = false;
    let anchorTextBudgetSkip = false;
    if (kv && currentBudgetRows !== null) {
      const baProjected = brandAuthorityEntitled ? 1 + MOZ_MAX_BA_POLLS : 0;
      const listLimit = MOZ_AUTONOMOUS_LIST_LIMIT;
      // Available budget for list methods after reserving site-metrics + BA.
      let availableForLists = Math.max(
        0,
        MOZ_DAILY_TENANT_ROW_BUDGET - currentBudgetRows - 1 - baProjected,
      );
      // Drop methods in REVERSE priority order until the projection fits.
      const wouldDispatchAnchorText =
        anchorTextEntitled || anchorTextEntitledIsUnknown;
      const desiredAnchorTextCost = wouldDispatchAnchorText ? listLimit : 0;
      const desiredTotal = listLimit /* top-pages */ + listLimit /* linking-root */
        + desiredAnchorTextCost;

      if (desiredTotal > availableForLists) {
        // Step 1 — drop anchor-text (lowest priority).
        if (wouldDispatchAnchorText) {
          anchorTextBudgetSkip = true;
          emitStructuredLog("moz_anchor_text_budget_skip", {
            tenantId: ctx.tenantId,
            integrationId: ctx.integrationId,
            target,
            projectedCost: listLimit,
            remainingBudget: availableForLists,
          });
        }
        // Step 2 — re-check after dropping anchor-text.
        const remainingAfterAnchorText = listLimit + listLimit; // top-pages + linking-root
        if (remainingAfterAnchorText > availableForLists) {
          // Drop linking-root-domains (next-lowest priority).
          linkingRootDomainsBudgetSkip = true;
        }
        // Step 3 — re-check after dropping linking-root-domains.
        if (linkingRootDomainsBudgetSkip) {
          const remainingAfterLrd = listLimit; // top-pages only
          if (remainingAfterLrd > availableForLists) {
            // Drop top-pages last.
            topPagesBudgetSkip = true;
          }
        }
      }
    }
    const anchorTextShouldDispatch =
      !anchorTextBudgetSkip && (anchorTextEntitled || anchorTextEntitledIsUnknown);

    // CR Round 2 #5 fix — capture timing for `moz_anchor_text_fetched`
    // durationMs per AC #18. Stamped immediately before the Promise.all
    // dispatch so the latency reflects the full network round-trip + any
    // retries inside the helper.
    const anchorTextStartedAt = anchorTextShouldDispatch ? Date.now() : 0;

    const [siteMetricsR, brandAuthorityR, topPagesR, linkingRdR, anchorTextR] = await Promise.all([
      fetchMozSiteMetrics({
        ...callOpts,
        target,
        scope,
        indexId,
      }).catch((err: unknown) => err),
      brandAuthorityEntitled
        ? fetchMozBrandAuthority({
            ...callOpts,
            target,
            indexId,
          }).catch((err: unknown) => err)
        : Promise.resolve(null),
      topPagesBudgetSkip
        ? Promise.resolve(null)
        : fetchMozTopPages({
            ...callOpts,
            target,
            scope,
            limit: MOZ_AUTONOMOUS_LIST_LIMIT,
            indexId,
          }).catch((err: unknown) => err),
      linkingRootDomainsBudgetSkip
        ? Promise.resolve(null)
        : fetchMozLinkingRootDomains({
            ...callOpts,
            target,
            scope,
            limit: MOZ_AUTONOMOUS_LIST_LIMIT,
            indexId,
          }).catch((err: unknown) => err),
      anchorTextShouldDispatch
        ? fetchMozAnchorText({
            ...callOpts,
            target,
            scope,
            sort: MOZ_AUTONOMOUS_ANCHOR_TEXT_SORT,
            limit: MOZ_AUTONOMOUS_LIST_LIMIT,
            indexId,
          }).catch((err: unknown) => err)
        : Promise.resolve(null),
    ]);

    // ── Process site-metrics result (defer snapshot write until AFTER BA) ──
    let siteMetricsRow: Record<string, unknown> | null = null;
    if (
      siteMetricsR &&
      typeof siteMetricsR === "object" &&
      "records" in siteMetricsR &&
      Array.isArray((siteMetricsR as { records: unknown }).records)
    ) {
      const r = siteMetricsR as { records: Record<string, unknown>[]; rowsCharged: number };
      records.push(...r.records);
      totalRowsCharged += r.rowsCharged;
      siteMetricsRow = r.records[0] ?? null;
    } else {
      warnings.push({ reportType: "site-metrics", reason: "fetch_failed" });
    }

    // ── Process Brand Authority result (or skipped marker per AC #4) ──
    let brandAuthorityScore: number | null = null;
    if (brandAuthorityR === null) {
      warnings.push({
        reportType: "brand-authority",
        reason: "subscription_required",
      });
    } else if (
      brandAuthorityR &&
      typeof brandAuthorityR === "object" &&
      "records" in brandAuthorityR &&
      Array.isArray((brandAuthorityR as { records: unknown }).records)
    ) {
      const r = brandAuthorityR as {
        records: Record<string, unknown>[];
        rowsCharged: number;
      };
      records.push(...r.records);
      totalRowsCharged += r.rowsCharged;
      const baRow = r.records[0];
      const baValue = baRow?.brandAuthorityScore;
      if (typeof baValue === "number" && Number.isFinite(baValue)) {
        brandAuthorityScore = baValue;
      } else {
        // **Round 2 review fix:** BA probe succeeded structurally but the
        // score field was null/undefined — Moz's "data not yet ready"
        // envelope after async-call resolution. Surface a warning so
        // observability can detect the wasted polling cost.
        warnings.push({
          reportType: "brand-authority",
          reason: "score_null_after_resolve",
        });
      }
    } else {
      warnings.push({ reportType: "brand-authority", reason: "fetch_failed" });
    }

    // ── AC #16 — fire-and-forget snapshot persistence ──
    // Round 1 review fixes:
    //   - Defer snapshot write until AFTER Brand Authority is processed so the
    //     `brandAuthorityScore` column is actually populated when entitled
    //     (was hard-coded to null before; Story 20.9's BA-trend chart was broken).
    //   - Refuse to write the snapshot when DA or PA is null (was falling back
    //     to 0, polluting historical charts with phantom "DA dropped to 0!" rows).
    //   - Refuse to write when indexId is "unknown" — would collapse multiple
    //     periods' worth of snapshots to a single row via the dedup index
    //     (whole months silently lost). Better to skip than corrupt the history.
    const db = getAdapterDb(ctx);
    const indexIdLooksUnknown = indexId === "unknown" || indexId.startsWith("unknown_");
    // **Round 2 + 3 review fix:** require DA OR PA (not BOTH). Moz's authority
    // scale is 1-100 — `0` represents an envelope bug or uncrawled domain,
    // NOT a real authority value. Round 3 finding: the previous `typeof ===
    // "number"` check accepted 0 as legitimate, then the fallback wrote 0 as
    // page-authority for the snapshot, polluting Story 20.9's trend chart
    // with phantom "PA dropped to 0" data points. Treat `<= 0` as missing.
    const da =
      typeof siteMetricsRow?.domainAuthority === "number" &&
      siteMetricsRow.domainAuthority > 0
        ? (siteMetricsRow.domainAuthority as number)
        : null;
    const pa =
      typeof siteMetricsRow?.pageAuthority === "number" &&
      siteMetricsRow.pageAuthority > 0
        ? (siteMetricsRow.pageAuthority as number)
        : null;
    const hasDA = da !== null;
    const hasPA = pa !== null;
    const canPersistSnapshot =
      db !== undefined &&
      siteMetricsRow !== null &&
      !indexIdLooksUnknown &&
      (hasDA || hasPA);
    if (canPersistSnapshot && db && siteMetricsRow) {
      persistMozSnapshotFireAndForget(getCtxWaitUntil(ctx), db, {
        tenantId: ctx.tenantId,
        integrationId: ctx.integrationId,
        target,
        scope,
        indexId,
        capturedAt: Date.now(),
        // Schema NOT NULL — when only one of DA/PA was valid (>0), fall back
        // to the other (for page-only scopes Moz returns PA without DA, and
        // vice versa). The fallback values are guaranteed >0 by the gating
        // check above, so the snapshot row never carries a phantom 0.
        domainAuthority: (da ?? pa) as number,
        pageAuthority: (pa ?? da) as number,
        spamScore: (siteMetricsRow.spamScore as number | null) ?? null,
        brandAuthorityScore,
        linkPropensity: (siteMetricsRow.linkPropensity as number | null) ?? null,
        pagesToPage: (siteMetricsRow.pagesToPage as number | null) ?? null,
        rootDomainsToPage:
          (siteMetricsRow.rootDomainsToPage as number | null) ?? null,
      });
    } else if (siteMetricsRow !== null && indexIdLooksUnknown) {
      // Surface the skip so observability shows why the snapshot table is
      // missing rows for this period.
      warnings.push({
        reportType: "site-metrics",
        reason: "snapshot_skipped_unknown_index_id",
      });
    } else if (siteMetricsRow !== null && !hasDA && !hasPA) {
      // Both DA AND PA missing — neither can fall back. Skip snapshot.
      warnings.push({
        reportType: "site-metrics",
        reason: "snapshot_skipped_invalid_authority",
      });
    } else if (db === undefined && siteMetricsRow !== null) {
      // Round 2 review fix — surface DB-missing case for observability so
      // operators can distinguish "no snapshots written" from "snapshots
      // skipped for valid reasons".
      warnings.push({
        reportType: "site-metrics",
        reason: "snapshot_skipped_no_db_binding",
      });
    }

    // Process Top Pages result (CR Round 2 #2 — symmetric budget skip)
    if (topPagesR === null) {
      warnings.push({ reportType: "top-pages", reason: "rate_limited" });
    } else if (
      topPagesR &&
      typeof topPagesR === "object" &&
      "records" in topPagesR &&
      Array.isArray((topPagesR as { records: unknown }).records)
    ) {
      const r = topPagesR as { records: Record<string, unknown>[]; rowsCharged: number };
      records.push(...r.records);
      totalRowsCharged += r.rowsCharged;
    } else {
      warnings.push({ reportType: "top-pages", reason: "fetch_failed" });
    }

    // Process Linking Root Domains result (CR Round 2 #2 — symmetric budget skip)
    if (linkingRdR === null) {
      warnings.push({ reportType: "linking-root-domains", reason: "rate_limited" });
    } else if (
      linkingRdR &&
      typeof linkingRdR === "object" &&
      "records" in linkingRdR &&
      Array.isArray((linkingRdR as { records: unknown }).records)
    ) {
      const r = linkingRdR as { records: Record<string, unknown>[]; rowsCharged: number };
      records.push(...r.records);
      totalRowsCharged += r.rowsCharged;
    } else {
      warnings.push({ reportType: "linking-root-domains", reason: "fetch_failed" });
    }

    // ── Story 35.2 — Process Anchor Text result + entitlement persistence ──
    // 5-branch decision tree (mirrors AC #5 + AC #6 + AC #12 per CR Round 1 #2/#3):
    //   0. anchorTextBudgetSkip === true → SKIP with rate_limited warning;
    //      `moz_anchor_text_budget_skip` event already emitted at decision time.
    //   1. anchorTextR === null  → skipped (anchorTextEntitled === false);
    //      emit subscription_required warning + no JSON-RPC was issued.
    //   2. anchorTextR has `records[]` → SUCCESS. If we just probed (was
    //      undefined), persist entitlement=true via fire-and-forget.
    //   3. anchorTextR is a 403 with plan/subscription/tier/entitle in
    //      code or message → SUBSCRIPTION_REQUIRED. If we just probed,
    //      persist entitlement=false via fire-and-forget (saves future row
    //      spend). Emit warning.
    //   4. anchorTextR is any other error (5xx, non-classified 403, sync
    //      throw) → fetch_failed warning + DO NOT persist entitlement
    //      (preserves `undefined` for retry on next call).
    if (anchorTextBudgetSkip) {
      // Branch 0 — daily row budget would breach; defer anchor-text fetch.
      // Other 4 methods still fetched (they were already dispatched in the
      // Promise.all before this check is consulted — but the projected-cost
      // arithmetic accounts for them, so the only safe skip is anchor-text).
      warnings.push({
        reportType: "anchor-text",
        reason: "rate_limited",
      });
    } else if (anchorTextR === null) {
      // Branch 1 — already known not-entitled; surface upsell badge.
      warnings.push({
        reportType: "anchor-text",
        reason: "subscription_required",
      });
    } else if (
      anchorTextR &&
      typeof anchorTextR === "object" &&
      "records" in anchorTextR &&
      Array.isArray((anchorTextR as { records: unknown }).records)
    ) {
      // Branch 2 — happy path.
      const r = anchorTextR as {
        records: Record<string, unknown>[];
        rowsCharged: number;
      };
      records.push(...r.records);
      totalRowsCharged += r.rowsCharged;
      // Per-call structured-log telemetry (AC #18; CR Round 2 #5 adds durationMs).
      emitStructuredLog("moz_anchor_text_fetched", {
        tenantId: ctx.tenantId,
        integrationId: ctx.integrationId,
        target,
        scope,
        sort: MOZ_AUTONOMOUS_ANCHOR_TEXT_SORT,
        limit: MOZ_AUTONOMOUS_LIST_LIMIT,
        rowCount: r.records.length,
        rowsCharged: r.rowsCharged,
        durationMs: Date.now() - anchorTextStartedAt,
      });
      // First-call probe — persist entitlement=true (was undefined).
      if (anchorTextEntitledIsUnknown && kv && ctx.integrationId) {
        const wu = getCtxWaitUntil(ctx);
        const persistPromise = persistMozMetadata(kv, ctx.integrationId, {
          anchorTextEntitled: true,
        });
        if (wu) wu(persistPromise);
        else void persistPromise;
        emitStructuredLog("moz_anchor_text_entitlement_probed", {
          tenantId: ctx.tenantId,
          integrationId: ctx.integrationId,
          target,
          entitled: true,
        });
      }
    } else if (classifyMozSubscriptionRequired403(anchorTextR).matched) {
      // Branch 3 — subscription_required classified 403. CR Round 3 #7 fix:
      // confidence-aware persistence. HIGH confidence (primary code-path) →
      // persist `anchorTextEntitled: false` to short-circuit future calls.
      // LOW confidence (message-only fallback) → warn-only, do NOT persist;
      // defends against 24h lockout from transient verification 403s.
      const classification = classifyMozSubscriptionRequired403(anchorTextR);
      warnings.push({
        reportType: "anchor-text",
        reason: "subscription_required",
      });
      emitStructuredLog("moz_anchor_text_subscription_required", {
        tenantId: ctx.tenantId,
        integrationId: ctx.integrationId,
        target,
        confidence: classification.confidence,
      });
      if (
        classification.confidence === "high" &&
        anchorTextEntitledIsUnknown &&
        kv &&
        ctx.integrationId
      ) {
        const wu = getCtxWaitUntil(ctx);
        const persistPromise = persistMozMetadata(kv, ctx.integrationId, {
          anchorTextEntitled: false,
        });
        if (wu) wu(persistPromise);
        else void persistPromise;
      }
    } else {
      // Branch 4 — 5xx, non-classified 403, sync throw, or unknown shape.
      // Do NOT persist entitlement; preserve `undefined` for retry.
      warnings.push({ reportType: "anchor-text", reason: "fetch_failed" });
    }

    // Increment per-tenant + per-integration row counters using the SAME
    // `todayUtc` bucket the check used (UTC midnight rollover race fix).
    if (kv && totalRowsCharged > 0) {
      try {
        await incrementMozRowsUsed(
          kv,
          ctx.tenantId,
          ctx.integrationId,
          totalRowsCharged,
          todayUtc,
        );
      } catch {
        // KV write failure must NOT block the response; the budget is a soft
        // safety net (Moz's server-side quota is the hard cap).
      }
    }

    // Per-call telemetry
    emitStructuredLog("moz_api_call_summary", {
      tenantId: ctx.tenantId,
      integrationId: ctx.integrationId,
      target,
      scope,
      indexId,
      recordCount: records.length,
      rowsCharged: totalRowsCharged,
      warningCount: warnings.length,
    });

    return adapterOk({
      platform: PLATFORM,
      accountId: target,
      dateRange,
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
      warnings: warnings.length > 0 ? warnings : undefined,
      metadata: {
        scope,
        indexId,
        rowsCharged: totalRowsCharged,
        brandAuthorityEntitled,
      },
    });
  },

  async discoverAccounts(
    ctx: AdapterContext,
    _credentials: OAuthCredentials,
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Mirrors PageSpeed / Ahrefs / Semrush pattern — Moz has no concept of
    // "accounts" or "properties"; the target IS the account. Surface the
    // configured target as a single AccountInfo; empty list when no target.
    const accountId = ctx.accountId;
    if (!accountId) {
      return adapterOk([]);
    }
    return adapterOk([
      {
        id: accountId,
        name: accountId,
        platform: PLATFORM,
      },
    ]);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return MOZ_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return MOZ_DIMENSIONS;
  },

  /**
   * AC #14 — per-endpoint availability snapshot. The Brand Authority
   * entitlement is read from the integration's metadata JSON (set at connect
   * time per AC #2). Two read paths:
   *
   *   1. **Primary** — `ctx.metadata.brandAuthorityEntitled` (route-handler-attached).
   *   2. **Fallback** — KV side-channel `moz:meta:{id}`.
   */
  async discoverEndpoints(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
  ): Promise<AdapterResult<EndpointDiscoveryInfo>> {
    const apiToken = credentials.accessToken;
    if (!apiToken || apiToken.length === 0) {
      return adapterErr({
        code: "token_invalid",
        message: "Moz API token is missing or empty.",
        retryable: false,
      });
    }
    const ctxMetadata =
      (ctx as unknown as { metadata?: Record<string, unknown> }).metadata ?? {};
    let brandAuthorityEntitled = ctxMetadata.brandAuthorityEntitled === true;
    // Story 35.2 — Anchor Text entitlement read. Mirrors brand-authority
    // path but with three states: undefined (unprobed → "available" as the
    // default; first widget-config dispatch triggers the lazy probe),
    // true (known entitled), false (known not-entitled → upsell badge).
    let anchorTextEntitledKnown =
      typeof ctxMetadata.anchorTextEntitled === "boolean";
    let anchorTextEntitled = ctxMetadata.anchorTextEntitled === true;

    if (
      ctxMetadata.brandAuthorityEntitled === undefined ||
      ctxMetadata.anchorTextEntitled === undefined
    ) {
      const kv = getAdapterKv(ctx);
      if (kv && ctx.integrationId) {
        try {
          const raw = await kv.get(`moz:meta:${ctx.integrationId}`);
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              if (
                parsed &&
                typeof parsed === "object" &&
                !Array.isArray(parsed)
              ) {
                const obj = parsed as Record<string, unknown>;
                if (
                  ctxMetadata.brandAuthorityEntitled === undefined &&
                  obj.brandAuthorityEntitled === true
                ) {
                  brandAuthorityEntitled = true;
                }
                if (
                  ctxMetadata.anchorTextEntitled === undefined &&
                  typeof obj.anchorTextEntitled === "boolean"
                ) {
                  anchorTextEntitledKnown = true;
                  anchorTextEntitled = obj.anchorTextEntitled === true;
                }
              }
            } catch {
              // Malformed JSON — leave at default.
            }
          }
        } catch {
          // KV read failure must NOT block discoverEndpoints.
        }
      }
    }

    const brandAuthorityMeta = brandAuthorityEntitled
      ? {}
      : {
          subscriptionRequired: "Moz API ≥ $20/mo plan",
          setupHint:
            "Brand Authority requires a Moz API plan with the Brand Authority entitlement. Upgrade at https://meilu1.jpshuntong.com/url-68747470733a2f2f6d6f7a2e636f6d/products/api/pricing.",
        };

    // Story 35.2 — Anchor Text status:
    //   - known-true → "available"
    //   - known-false → "subscription_required" (upsell badge)
    //   - unknown (never probed) → "available" (lazy first-call probe will
    //     flip the metadata on the next fetchMetrics dispatch).
    //
    // **CR Round 1 #6 (HIGH) — accepted trade-off.** Defaulting unknown
    // entitlement to "available" allows a Free-tier customer to configure an
    // anchor-text widget that will degrade gracefully on first fetchMetrics
    // (subscription_required warning + upsell). The alternative — probing in
    // discoverEndpoints — would cost 1 row per connect even on customers who
    // never use anchor-text. We accept the small "configures widget then sees
    // upsell" UX hiccup in exchange for the row savings, because:
    //   (a) anchor-text is supplementary (not flagship like Brand Authority),
    //   (b) Free-tier customers are unlikely to consume the widget heavily,
    //   (c) the 4-branch orchestrator decision tree below flips
    //       `anchorTextEntitled: false` after the first 403 and the widget's
    //       subsequent renders cleanly show the upsell.
    // Brand Authority took the OPPOSITE trade-off (eager probe at connect,
    // 1 row spent upfront) because it's a flagship signal. Document the
    // divergence here.
    const anchorTextStatus: "available" | "subscription_required" =
      anchorTextEntitledKnown && !anchorTextEntitled
        ? "subscription_required"
        : "available";
    const anchorTextMeta =
      anchorTextStatus === "subscription_required"
        ? {
            subscriptionRequired: "Moz API Standard plan or higher",
            setupHint:
              "Anchor Text requires a Moz API Standard plan or higher (~$500/mo). Upgrade at https://meilu1.jpshuntong.com/url-68747470733a2f2f6d6f7a2e636f6d/products/api/pricing. Free + Starter tiers do not include this endpoint.",
          }
        : {};

    return adapterOk({
      endpoints: [
        { key: "site-metrics", status: "available" as const },
        { key: "top-pages", status: "available" as const },
        { key: "linking-root-domains", status: "available" as const },
        {
          key: "brand-authority",
          status: brandAuthorityEntitled
            ? ("available" as const)
            : ("subscription_required" as const),
          ...brandAuthorityMeta,
        },
        {
          key: "anchor-text",
          status: anchorTextStatus,
          ...anchorTextMeta,
        },
      ],
    });
  },
};

// ============================================================================
// Re-exports for the public surface (route handler + tests use these).
// ============================================================================

export {
  validateMozConnection,
  normalizeMozTarget,
  sanitizeMozErrorMessage,
  isMozScope,
  MOZ_LIST_LIMIT_MAX,
  MOZ_DAILY_TENANT_ROW_BUDGET,
};
export type { MozScope };

// Re-export Platform-typed constant for adapter.platform consumers
export const MOZ_PLATFORM: Platform = PLATFORM;
